import { getItemPrice, roundAmount } from '@rogue/universal'
import type { Pinia } from 'pinia'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useProductStore } from '@/js/stores/productStore'
import { BuyRequest, HotDeal, IRogue } from '@/js/services/RogueTypes'

type ComputePriceOpts = {
  parent?: BuyRequest
  single?: boolean,
  ignorePromo?: boolean
}

export function computePriceSync(buyRequest: BuyRequest, pinia: Pinia, opts? : ComputePriceOpts): number {
  const productStore = useProductStore(pinia)

  const repo = {
    getProductById: (id: number) => {
      const product = productStore.products[id]
      // If we want to compute the price without the promo, then we need to duplicate the product...
      if (opts?.ignorePromo && (product.attributes.rogue_promo_price || product.attributes.special_price)) {
        const attrsWithoutPromo = Object.assign({}, product.attributes, { rogue_promo_price: undefined, special_price: undefined })

        return Object.assign({}, product, { attributes: attrsWithoutPromo })
      }

      return product
    },
  }

  if (buyRequest.metadata?.freeBundleOptionId) {
    return 0
  }

  const prod = repo.getProductById(buyRequest.productId)

  // Unless we can ensure that all products are loaded before calling this, it might error...
  if (prod === undefined) {
    throw new Error('Trying to get price for not loaded product')
  }

  if (prod.type === 'configurable' && !buyRequest.children) {
    throw new Error('Trying to get price for configurable with no children')
  }

  try {
    if (prod.type === 'grouped' || buyRequest.isEvent) {
    // Grouped product buy requests shouldn't be added to lists,
    // but being able to send them to this function is convenient for displaying totals.
      return (buyRequest.children || [])
        .filter(child => Boolean(child) && child.qty > 0)
        .reduce((acc, child) => acc + buyRequest.qty * computePriceSync(child, pinia, { ...opts, parent: buyRequest }), 0)
    }

    if (buyRequest.qty === 0) {
      return 0
    }

    const customerStore = useCustomerStore(pinia)
    const groupId = customerStore.groupId
    const earnedProducts = customerStore.activeEarnedProducts || {}

    return roundAmount(buyRequest.qty * getItemPrice(buyRequest, groupId, repo, opts?.parent, earnedProducts))
  } catch (e) {
    throw new Error('Unable to return a price for the product')
  }
}

export async function computePrice(buyRequest: BuyRequest, rogue: IRogue): Promise<number> {
  const ids: number[] = []

  const productStore = useProductStore(rogue.pinia)

  const addChildren = (buyReq: BuyRequest) => {
    if (!productStore.products[buyReq.productId] && !productStore.errors[buyReq.productId]) {
      ids.push(buyReq.productId)
    }
    if (buyReq.children) {
      buyReq.children.forEach(addChildren)
    }
  }

  addChildren(buyRequest)

  if (ids.length > 0) {
    await productStore.loadAll(ids, rogue)
  }

  return computePriceSync(buyRequest, rogue.pinia!)
}

interface DefaultPrice {
  productId: number,
  price?: number,
  specialPrice: number | null,
  includingPrice?: number,
  includingSpecialPrice?: number
}
